import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { formatInTimeZone } from 'date-fns-tz';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext from '@sdk/lang/gettext';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import getRealDate from '@sdk/utils/getRealDate';
import type {
  TimesheetHistoryItemFromApi,
  TimesheetHistoryItem,
  TimesheetHistoryTimeEntry,
  TimesheetHistoryClockItem,
} from '@sdk/timesheets/types/TimesheetHistory';
import { TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { HistorySpecificType } from '@sdk/timesheets/types/TimesheetHistory';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';
import type { TimesheetProjection } from '@sdk/timesheets/types/TimesheetProjection';
import { AutomatedBreakType } from '@sdk/settings/types';
import { UserBasic } from '@sdk/users/types/User';

type TimesheetHistoryItemWithGroupKeys = Omit<
  TimesheetHistoryItemFromApi,
  'from' | 'to' | 'user'
> & {
  user: UserBasic;
  fromDate?: Date;
  toDate?: Date;
  fromStr?: string;
  toStr?: string;
  when: Date;
  whenStr: string;
  groupKey: string;
};

const mapWithGroupKeys = (
  fromServer: TimesheetHistoryItemFromApi,
  personasDict: PersonasDict,
  currentUser: AuthUserType
): TimesheetHistoryItemWithGroupKeys => {
  const whenDateString = fromServer.edited || fromServer.to;

  const { from, to, ...restFromServer } = fromServer;

  const result: Partial<TimesheetHistoryItemWithGroupKeys> = {
    ...restFromServer,
    user: (personasDict[fromServer.user.id] || fromServer.user) as UserBasic,
    when: getRealDate(whenDateString),
    whenStr: formatInTimeZone(
      whenDateString,
      currentUser.timezone,
      `MMM d • ${currentUser.settings.usTimeFormat ? 'hh:mm a' : 'HH:mm'} zzz`
    ),
  };

  if (from) {
    try {
      result.fromDate = getRealDate(fromServer.from);
    } catch {
      result.fromStr = fromServer.from;
    }
  }

  if (to) {
    try {
      result.toDate = getRealDate(fromServer.to);
    } catch {
      result.toStr = fromServer.to;
    }
  }

  result.groupKey = `${result.user.id};${whenDateString}`;

  return result as TimesheetHistoryItemWithGroupKeys;
};

const parseItemsPerProjection = (
  itemsPerProjection: TimesheetHistoryItemWithGroupKeys[],
  results: TimesheetHistoryTimeEntry[]
) => {
  const sorted = sortBy(itemsPerProjection, [
    (x) =>
      getRealDate(
        x.toEntry.type === TimesheetEntryType.deleteParent
          ? x.prevEntry.timestamp
          : x.toEntry.timestamp
      ),
  ]);
  const timesheetHistoryTimeEntry: Partial<TimesheetHistoryTimeEntry> = {
    breaks: [],
    breakEnds: [],
  };

  sorted.forEach(({ toEntry, prevEntry, type }) => {
    const isToEntry = toEntry && toEntry.type !== TimesheetEntryType.deleteParent;

    if (type === TimesheetEntryType.clockIn) {
      timesheetHistoryTimeEntry.clockInFrom = prevEntry
        ? getRealDate(prevEntry.timestamp)
        : undefined;
      timesheetHistoryTimeEntry.clockInTo = isToEntry ? getRealDate(toEntry.timestamp) : undefined;
    }

    if (type === TimesheetEntryType.breakStart) {
      timesheetHistoryTimeEntry.breaks.push({
        type: isToEntry ? toEntry.automatedBreakType : prevEntry.automatedBreakType,
        startFrom: prevEntry ? getRealDate(prevEntry.timestamp) : undefined,
        startTo: isToEntry ? getRealDate(toEntry.timestamp) : undefined,
      });
    }

    if (type === TimesheetEntryType.breakEnd) {
      const breakObj =
        timesheetHistoryTimeEntry.breaks[timesheetHistoryTimeEntry.breaks.length - 1];
      if (
        breakObj &&
        (!breakObj.startFrom || // matching ADDED break start
          (timesheetHistoryTimeEntry.clockInFrom && !timesheetHistoryTimeEntry.clockInTo) || // or we're deleting the entire time entry
          (breakObj.startFrom && !breakObj.startTo)) // or we're deleting the entire break
      ) {
        breakObj.endFrom = prevEntry ? getRealDate(prevEntry.timestamp) : undefined;
        breakObj.endTo = isToEntry ? getRealDate(toEntry.timestamp) : undefined;
      } else {
        timesheetHistoryTimeEntry.breakEnds.push({
          type: isToEntry ? toEntry.automatedBreakType : prevEntry.automatedBreakType,
          endFrom: prevEntry ? getRealDate(prevEntry.timestamp) : undefined,
          endTo: isToEntry ? getRealDate(toEntry.timestamp) : undefined,
        });
      }
    }

    if (type === TimesheetEntryType.clockOut || type === TimesheetEntryType.autoClockOut) {
      timesheetHistoryTimeEntry.clockOutFrom = prevEntry
        ? getRealDate(prevEntry.timestamp)
        : undefined;
      timesheetHistoryTimeEntry.clockOutTo = isToEntry ? getRealDate(toEntry.timestamp) : undefined;
    }
  });

  results.push(timesheetHistoryTimeEntry as TimesheetHistoryTimeEntry);
};

const mapTimeclockChanges = (
  groupItems: TimesheetHistoryItemWithGroupKeys[]
): TimesheetHistoryClockItem[] => {
  return groupItems.map((item: TimesheetHistoryItemWithGroupKeys): TimesheetHistoryClockItem => {
    if (item.type === TimesheetEntryType.clockIn) {
      return { text: gettext('Clocked into the shift') };
    }

    if (item.type === TimesheetEntryType.breakStart) {
      if (!item.toEntry.automatedBreakType) {
        return { text: gettext('Started break') };
      }

      return {
        text:
          item.toEntry.automatedBreakType === AutomatedBreakType.meal
            ? gettext('Started meal break')
            : gettext('Started rest break'),
      };
    }

    if (item.type === TimesheetEntryType.breakEnd) {
      if (!item.toEntry.automatedBreakType) {
        return { text: gettext('Ended break') };
      }

      return {
        text:
          item.toEntry.automatedBreakType === AutomatedBreakType.meal
            ? gettext('Ended meal break')
            : gettext('Ended rest break'),
      };
    }

    if (item.type === HistorySpecificType.assigneeNotes) {
      if (!item.toStr) {
        return {
          text: gettext('Deleted commment'),
          component: <Text14 themeColor={ThemeColorType.danger}>{item.fromStr}</Text14>,
        };
      }

      const text = item.fromStr ? gettext('Edited commment') : gettext('Added comment');
      const component = item.fromStr ? (
        <Text14 themeColor={ThemeColorType.gray}>
          <TranslateWithComponents
            replaceComponents={{
              from: <Text14 themeColor={ThemeColorType.danger}>{item.fromStr}</Text14>,
              to: <Text14 themeColor={ThemeColorType.success}>{item.toStr}</Text14>,
            }}>
            {gettext('From [[from]] to [[to]]')}
          </TranslateWithComponents>
        </Text14>
      ) : (
        <Text14 themeColor={ThemeColorType.success}>{item.toStr}</Text14>
      );

      return {
        text,
        component,
      };
    }

    if (item.type === TimesheetEntryType.clockOut) {
      const result: Partial<TimesheetHistoryClockItem> = {
        text: gettext('Clocked out of the shift'),
      };

      if (item.attestations) {
        result.breakAttestations = item.attestations;
      }

      return result as TimesheetHistoryClockItem;
    }

    if (item.type === TimesheetEntryType.autoClockOut) {
      return { text: gettext('Was automatically clocked out of the shift') };
    }

    if (item.type === TimesheetEntryType.markApproved) {
      return { text: gettext('Approved the timesheet') };
    }

    if (item.type === TimesheetEntryType.markPending) {
      return { text: gettext('Marked the timesheet as pending') };
    }

    return { text: '' };
  });
};

// require every item in the group to be actor-unknown, not just one, since a group can
// bundle several entries (e.g. clock-in + a break) submitted together by the same actor
const isGroupFromUnknownActor = (groupItems: TimesheetHistoryItemWithGroupKeys[]): boolean =>
  groupItems.every(
    (item) => item.toEntry?.isActorUnknown ?? item.prevEntry?.isActorUnknown ?? false
  );

export const mapTimesheetHistory = (
  data: TimesheetHistoryItemFromApi[],
  personasDict: PersonasDict,
  currentUser: AuthUserType
): TimesheetHistoryItem[] => {
  const timesheetHistoryItemsWithGroupKeys = data.map((i) =>
    mapWithGroupKeys(i, personasDict, currentUser)
  );

  const groupedAndSorted = sortBy(
    Object.values(groupBy(timesheetHistoryItemsWithGroupKeys, 'groupKey')),
    [(a) => a[0].when]
  );

  const parsed = groupedAndSorted.map((groupItems) => {
    // isActorUnknown is platform-agnostic (Toast, Square, Shift4), so it's only safe to show
    // the "Toast POS" label when the org's active integration is actually Toast
    const user =
      currentUser.org.hasToastIntegration && isGroupFromUnknownActor(groupItems)
        ? { ...groupItems[0].user, fullname: gettext('Toast POS') }
        : groupItems[0].user;

    const result: Partial<TimesheetHistoryItem> = {
      user,
      when: groupItems[0].when,
      whenStr: groupItems[0].whenStr,
    };

    if (groupItems[0].isEdited) {
      const editedChanges: TimesheetHistoryTimeEntry[] = [];
      const groupedByProjection = groupBy(groupItems, (i) => i.toEntry.baseSequenceId);
      Object.values(groupedByProjection).forEach((i) => parseItemsPerProjection(i, editedChanges));

      result.editedChanges = editedChanges;
    } else {
      result.timeclockChanges = mapTimeclockChanges(groupItems);
    }

    return result as TimesheetHistoryItem;
  });

  return parsed;
};
